import { useState } from "react";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import AuthenticatedPageLayout from "@/components/layouts/AuthenticatedPageLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const ContactPage = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Message sent successfully! We'll get back to you soon.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setIsSubmitting(false);
  };

  const content = (
    <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
            Contact Us
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
            Get in Touch
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Have questions about our advisory services? We're here to help you navigate 
            your A.I. journey with confidence.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="glass p-8 rounded-2xl">
              <h2 className="font-heading text-2xl font-bold text-white mb-6">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-white mb-1">Email</p>
                    <p className="text-slate-400">info@aimeansbusiness.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-white mb-1">Phone</p>
                    <p className="text-slate-400">Available after engagement begins</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-white mb-1">Location</p>
                    <p className="text-slate-400">Serving clients globally</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass p-8 rounded-2xl">
              <h3 className="font-heading text-xl font-semibold text-white mb-4">
                Ready to Begin?
              </h3>
              <p className="text-slate-400 mb-4">
                Skip the contact form and get your free A.I. Business Evaluation today.
              </p>
              <a href="/get-started">
                <Button className="w-full bg-primary hover:bg-primary/90 text-white rounded-full">
                  Get Your Free Evaluation
                </Button>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass p-8 rounded-2xl">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">
              Send Us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-200">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  required
                  className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500"
                  data-testid="contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500"
                  data-testid="contact-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject" className="text-slate-200">Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="How can we help?"
                  required
                  className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500"
                  data-testid="contact-subject"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-200">Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us more about your inquiry..."
                  rows={5}
                  required
                  className="bg-navy-900/50 border-slate-700 focus:border-primary text-white placeholder:text-slate-500 resize-none"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6"
                data-testid="contact-submit"
              >
                {isSubmitting ? "Sending..." : (
                  <>
                    Send Message
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );

  // Render with authenticated layout if user is logged in
  if (user) {
    return <AuthenticatedPageLayout>{content}</AuthenticatedPageLayout>;
  }

  // Render with public layout for anonymous users
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      {content}
      <PublicFooter />
    </div>
  );
};

export default ContactPage;
